import React, { useContext, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../auth/AuthContext';
import PopoverCard from '../../components/PopoverCard';
import { margin, styled } from '@mui/system';
import { Box, Typography, Button, IconButton, Popover } from '@mui/material';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import BackgroundContainer from '../../components/BackgroundContainer';
import BaseHeader from '../../components/BaseHeader';
import BaseFormContainer from '../../components/BaseFormContainer';
import BaseTextField from '../../components/BaseTextField';
import BaseButton from '../../components/BaseButton';
import MenuButton from '../../components/MenuButton';


const Subtitle = styled(Typography)({
  fontFamily: 'Playfair Display, serif',
  fontSize: '1.05rem',
  color: '#ffbfe1',
  textAlign: 'center',
  textShadow: '0px 2px 6px rgba(0, 0, 0, 0.5)',
  lineHeight: '1.4',
});

const PayPalButtonWrapper = styled('div')({
  width: '280px',           // force equal width
  minHeight: '120px',       // makes room for both buttons
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
});

const PaymentPage = () => {
  const { auth, refreshAuth } = useContext(AuthContext);
  const [sdkReady, setSdkReady] = useState(false);
  const paypalRefPremium = useRef(null);
  const paypalRefSupreme = useRef(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [popoverAnchor, setPopoverAnchor] = useState(null);
  const [popoverContent, setPopoverContent] = useState('');
  const [verificationEmailPopover, setVerificationEmailPopover] = useState(false);

  const basePrices = {
    "Premium": 15.00,
    "Premium Supreme": 20.00,
  };

  const [displayPrices, setDisplayPrices] = useState(basePrices);

  const [redeemCode, setRedeemCode] = useState('');
  const [redeemMessage, setRedeemMessage] = useState(null);
  const [discountData, setDiscountData] = useState(null);

  const openPopover = (event, content) => {
    setPopoverAnchor(event.currentTarget);
    setPopoverContent(content);
  };

  const closePopover = () => {
    setPopoverAnchor(null);
    setPopoverContent('');
  };


  const getCSRFToken = () => {
    return document.cookie
      .split('; ')
      .find((row) => row.startsWith('csrftoken='))
      ?.split('=')[1];
  };

  useEffect(() => {
  }, [auth]);

  useEffect(() => {
    if (!auth?.data?.user?.membership) return;

    const userTier = auth.data.user.membership;

    // If user already has "Premium", Supreme should cost less
    if (userTier === "Premium") {
      setDisplayPrices(prev => ({
        ...prev,
        "Premium Supreme": 5.00,  // Discounted upgrade
      }));
    } else {
      setDisplayPrices(prev => ({
        ...prev,
        "Premium Supreme": basePrices["Premium Supreme"], // Reset to normal price
      }));
    }
  }, [auth?.data?.user?.membership]);

  useEffect(() => {
    const paypalScriptId = 'paypal-sdk-script';

    // Function to load the PayPal SDK script
    const loadPayPalScript = () => {
      const script = document.createElement('script');
      script.src = "https://www.paypal.com/sdk/js?client-id=BAA5yjMoWoViXNFYj72xntyU37EkUa2hr21z5-LhVwOXn4caxf1Flj2Sh108Y73pHIQ0MteMF5xLPIYGKs&currency=USD";
      script.id = paypalScriptId;
      script.async = true;
      script.onload = () => setSdkReady(true); // Indicate that the SDK is ready
      script.onerror = () => setError('Failed to load PayPal SDK. Please try again later.');
      document.body.appendChild(script);
    };

    // Load the PayPal SDK only if it's not already loaded
    if (!document.getElementById(paypalScriptId)) {
      loadPayPalScript();
    } else if (window.paypal) {
      setSdkReady(true); // If SDK is already loaded, set it as ready
    }
  }, []); // Runs once when the component mounts

  // Effect to initialize PayPal buttons after the SDK is ready
  useEffect(() => {
    if (!sdkReady) return;

    const initializePayPalButtons = () => {
      const renderButton = (ref, planName, price) => {

        if (ref && window.paypal) {
          window.paypal
            .Buttons({
              onClick: async (data, actions) => {
                setLoading(true);
                setError(null);

                if (!auth.data.user.email_verified) {
                  setVerificationEmailPopover(true); // Show popover instead of error
                  setLoading(false);
                  return actions.reject();
                }

                try {
                  const response = await fetch(
                    '/api/check-subscription-status/',
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFToken(),
                      },
                      body: JSON.stringify({ plan_name: planName }),
                      credentials: 'include',
                    }
                  );

                  const result = await response.json();

                  if (response.ok && result.can_purchase) {
                    return actions.resolve();
                  } else {
                    setError(result.message || 'You cannot purchase this plan.');
                    return actions.reject();
                  }
                } catch (err) {
                  console.error('Error checking subscription status:', err);
                  setError('Failed to verify subscription status.');
                  return actions.reject();
                } finally {
                  setLoading(false);
                }
              },
              createOrder: (data, actions) => {
                return actions.order.create({
                  purchase_units: [
                    {
                      amount: {
                        currency_code: 'USD',
                        value: price,
                        breakdown: {
                          item_total: {
                            currency_code: 'USD',
                            value: price,
                          },
                        },
                      },
                      items: [
                        {
                          name: planName,
                          unit_amount: {
                            currency_code: 'USD',
                            value: price,
                          },
                          quantity: '1',
                        },
                      ],
                      custom_id: auth?.data?.user?.id, // ✅ Pass user ID to PayPal
                      redeem_code: redeemCode?.trim() || null,
                    },
                  ],
                });
              },
              onApprove: async (data, actions) => {
                setLoading(true);
                try {
                  const order = await actions.order.capture();
                  const response = await fetch(
                    '/api/verify-payment/',
                    {
                      method: 'POST',
                      headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': getCSRFToken(),
                      },
                      body: JSON.stringify({
                        orderID: order.id,
                        redeemCode: redeemCode?.trim() || null,
                        // subscriptionID: order.subscriptionID # Add on Subscription button.
                      }),
                      credentials: 'include',
                    }
                  );

                  const result = await response.json();
                  setLoading(false);

                  if (result.success) {
                    // alert(Payment verified! Subscription updated to ${result.tier});
                    await refreshAuth()
                    navigate('/success');
                  } else {
                    setError(result.message || 'Payment verification failed.');
                  }
                } catch (err) {
                  setLoading(false);
                  setError('Something went wrong during payment verification.');
                }
              },
              onError: (err) => {
                console.error('Error:', err);
                setError('Payment process encountered an error.');
              },
            })
            .render(ref);
        }
      };

      // Render Premium button if user doesn't already have Premium
      if (auth.data.user.membership !== 'Premium' && paypalRefPremium.current) {
        paypalRefPremium.current.innerHTML = ''; // Cleanup before rendering
        const price = displayPrices["Premium"];
        renderButton(paypalRefPremium.current, 'Premium', price);
      }

      // Render Premium Supreme button if user doesn't already have Premium Supreme
      if (auth.data.user.membership !== 'Premium Supreme' && paypalRefSupreme.current) {
        paypalRefSupreme.current.innerHTML = ''; // Cleanup before rendering
        const supremePrice = displayPrices["Premium Supreme"];
        renderButton(paypalRefSupreme.current, 'Premium Supreme', supremePrice);
      }
    };

    initializePayPalButtons();

    const premiumNode = paypalRefPremium.current;
    const supremeNode = paypalRefSupreme.current;
    return () => {
      if (premiumNode) premiumNode.innerHTML = '';
      if (supremeNode) supremeNode.innerHTML = '';
    };
  }, [sdkReady, auth.data.user.membership, displayPrices]); // Runs when SDK is ready or subscription tier changes

  const handleRedeemCode = async () => {
    if (!redeemCode.trim()) {
      setRedeemMessage({ type: 'error', text: 'Please enter a code.' });
      return;
    }

    try {
      const response = await fetch('/api/codes/redeem-code/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRFToken': getCSRFToken(),
        },
        credentials: 'include',
        body: JSON.stringify({ code: redeemCode.trim() }),
      });

      const data = await response.json();

      if (response.ok) {
        if (data.type === 'percentage' || data.type === 'amount') {
          const discount = data.discounts[0] // assuming one discount for simplicity
          setDiscountData(discount);
          setDisplayPrices(prev => ({
            ...prev,
            [discount.product_name]: discount.final_price,
          }));
          setRedeemMessage({
            type: 'success',
            text: `Discount applied on: ${data.discounts[0].product_name}. New price: $${data.discounts[0].final_price}.`,
          });
        } else {
          if (paypalRefPremium.current) paypalRefPremium.current.innerHTML = '';
          if (paypalRefSupreme.current) paypalRefSupreme.current.innerHTML = '';

          setRedeemMessage({
            type: 'success',
            text: `Redeemed! Products granted: ${data.products.join(', ')}`,
          });
          navigate('/success-redeem')
        }
        await refreshAuth();
      } else {
        setRedeemMessage({ type: 'error', text: data.error || 'Code redemption failed.' });
      }
    } catch (err) {
      setRedeemMessage({ type: 'error', text: 'Something went wrong.' });
    }
  };


  return (
    <BackgroundContainer sx={{justifyContent: 'flex-start'}}>
            <MenuButton
            showLogin={true}
            showAccount={true}
            showDownload={true}
            />

      <BaseHeader sx={{
    fontSize: '3.5rem', // 🎯 Full size title
    '@media (max-width: 400px)': {
      fontSize: '3rem', // 🧠 Gentle scale-down on ultra-small screens
    },
    '@media (max-width: 360px)': {
      fontSize: '2.5rem', // 🔒 Last-resort fallback for smallest devices
    },
    textAlign: 'center', // (optional) center if you want
  }}>Choose Your Plan</BaseHeader>
      <Subtitle style={{
        color: 'rgba(233, 197, 231, 0.7)',
        fontSize: '1.3rem',
        marginBottom: '20px',
        textAlign: 'center',
      }}>
        Your current plan is - {auth.data.user.membership.replace('Plan', '').trim()}
      </Subtitle>

      {error && <Typography style={{ color: 'red', marginBottom: '10px' }}>{error}</Typography>}

      <BaseFormContainer sx={{ marginBottom: '20px' }}>
        {auth.data.user.membership !== 'Premium' && (
          <Box >
            <Box display="flex" alignItems="center" justifyContent="center">
              <Subtitle style={{
                color: 'rgba(233, 197, 231, 0.7)',
                fontSize: '1.3rem',
                marginBottom: '10px',
                textAlign: 'center',
                width: '100%'
              }}
              > Intimacy & Desire - ${displayPrices["Premium"] ? displayPrices["Premium"].toFixed(2) : "N/A"} </Subtitle>
              <IconButton onClick={(e) => openPopover(e, 'Enter the world of Deeper through two intertwining journeys — Intimacy, where connection and trust take root, and Desire, where playfulness and passion unfold. Each path compliments the other, guiding you deeper into exploration and connection.')}>
                <HelpOutlineIcon sx={{
                  fontSize: '1rem',
                  color: '#ffbfe5',
                  marginLeft: '4px',
                  marginBottom: '5px',
                  textShadow: '0 0 4px rgba(255, 191, 229, 0.3)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.15)',
                    color: '#ff99cc',
                  },
                }} />
              </IconButton>
            </Box>
            <PayPalButtonWrapper ref={paypalRefPremium}></PayPalButtonWrapper>
          </Box>
        )}

        {false && auth.data.user.membership !== 'Premium Supreme' && (
          <Box >
            <Box display="flex" alignItems="center" justifyContent="center">
              <Subtitle style={{
                color: 'rgba(233, 197, 231, 0.7)',
                fontSize: '1.3rem',
                textAlign: 'center',
                marginBottom: '10px',
              }}
              >Premium Supreme - ${displayPrices["Premium Supreme"] ? displayPrices["Premium Supreme"].toFixed(2) : "N/A"}</Subtitle>
              <IconButton onClick={(e) => openPopover(e, 'This is our premium Supreme that includes A, B, and C.')}>
                <HelpOutlineIcon sx={{
                  fontSize: '1rem',
                  color: '#ffbfe5',
                  marginBottom: '5px',
                  textShadow: '0 0 4px rgba(255, 191, 229, 0.3)',
                  transition: 'transform 0.2s ease',
                  '&:hover': {
                    transform: 'scale(1.15)',
                    color: '#ff99cc',
                  },
                }} />
              </IconButton>
            </Box>
            <PayPalButtonWrapper ref={paypalRefSupreme}></PayPalButtonWrapper>
          </Box>
        )}
      </BaseFormContainer>
      <BaseFormContainer sx={{ marginBottom: '20px' }}>
        <Subtitle style={{
          color: 'rgba(233, 197, 231, 0.7)',
          fontSize: '1.3rem',
          marginBottom: '20px',
          textAlign: 'center',
        }}
        >Redeem a Code</Subtitle>

        {redeemMessage && (
          <Typography
            style={{ color: redeemMessage.type === 'error' ? 'red' : '#00e676', marginBottom: '10px', textAlign: 'center' }}
          >
            {redeemMessage.text}
          </Typography>
        )}

        <BaseTextField
          type="text"
          value={redeemCode}
          onChange={(e) => setRedeemCode(e.target.value)}
          placeholder="Enter your code"
        />
        <BaseButton style={{ marginLeft: '10px' }} onClick={handleRedeemCode}>Redeem Code</BaseButton>
      </BaseFormContainer>

      <PopoverCard
        mainText={popoverContent}
        open={Boolean(popoverAnchor)}
        onClose={() => setPopoverAnchor(false)}
      />
      <PopoverCard
        mainText={'You need to verify your email in order to make a purchase.'}
        open={verificationEmailPopover}
        onClose={() => setVerificationEmailPopover(false)}
        showButtonOne={true}
        textButtonOne={'Go to verification page'}
        onButtonOne={() => navigate('/account/email')}
      />
        <Box sx={{ width: '65%', maxWidth: '300px', margin: '10px auto 20px', '& .MuiButton-root': { padding: '16px 36px !important', width: '100%' } }}>
        <BaseButton onClick={() => navigate('/account/profile')}>Back</BaseButton>
      </Box>
    </BackgroundContainer>
  );
};

export default PaymentPage;
